/**
 * Safety net: wrap each entry's root in this so a render error in one
 * surface doesn't blank the page for testers.
 *
 * Intentionally vanilla — no Tailwind, no icon library, no design-system
 * dependencies. Pure inline-style so it renders cleanly no matter which
 * theme the parent page is on.
 *
 * Unlike the v137 ErrorBoundary in src/components/ErrorBoundary.tsx (which
 * is Tailwind + lucide-react bound and currently broken in the build),
 * this one is intentionally minimal — designed to ALWAYS work, even when
 * everything else has fallen over.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { captureBoundaryError } from '@/lib/monitoring';

interface Props {
  /** Section name shown in the fallback header — "Wind-Drift Visualizer", "Build Review", etc. */
  section: string;
  children: ReactNode;
}

interface State {
  error: Error | null;
  copied: boolean;
}

export class SafeBoundary extends Component<Props, State> {
  override state: State = { error: null, copied: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    /* Console-log so the tester can paste the trace if needed. */
    // eslint-disable-next-line no-console
    console.error(`[SafeBoundary:${this.props.section}]`, error, info);
    /* Forward to Sentry. No-op if VITE_SENTRY_DSN isn't set. */
    captureBoundaryError(error, {
      componentStack: info.componentStack,
      section: this.props.section,
    });
  }

  reset = (): void => {
    this.setState({ error: null, copied: false });
  };

  copy = async (): Promise<void> => {
    const { error } = this.state;
    if (!error) return;
    const text = [
      `Section: ${this.props.section}`,
      `Time: ${new Date().toISOString()}`,
      `URL: ${typeof window !== 'undefined' ? window.location.href : 'n/a'}`,
      `UA: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a'}`,
      '',
      `Error: ${error.name}: ${error.message}`,
      '',
      'Stack:',
      error.stack ?? '(no stack)',
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      window.setTimeout(() => this.setState({ copied: false }), 2200);
    } catch {
      /* clipboard might be denied — silent fail */
    }
  };

  override render(): ReactNode {
    const { error, copied } = this.state;
    if (!error) return this.props.children;

    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <div style={styles.title}>Something broke in {this.props.section}.</div>
          <p style={styles.body}>
            The rest of the site is still working — only this section needs to reset.
            If this keeps happening, copy the details and email{' '}
            <a href="mailto:sal@sparrowexpeditions.com" style={styles.link}>
              sal@sparrowexpeditions.com
            </a>
            .
          </p>
          <div style={styles.actions}>
            <button style={styles.primary} onClick={this.reset}>
              Reset this section
            </button>
            <button style={styles.secondary} onClick={this.copy}>
              {copied ? 'Copied' : 'Copy error details'}
            </button>
            <a href="/wind-drift.html" style={styles.tertiary}>
              Back to Wind-Drift
            </a>
          </div>
          <details style={styles.details}>
            <summary style={styles.summary}>Technical details</summary>
            <pre style={styles.pre}>
{error.name}: {error.message}
{error.stack ?? '(no stack available)'}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    maxWidth: 640,
    margin: '40px auto',
    padding: '0 16px',
    fontFamily: 'system-ui, -apple-system, Segoe UI, sans-serif',
    color: '#18181b',
  },
  card: {
    background: '#fff',
    border: '1px solid #d4d4d8',
    borderLeft: '4px solid #b91c1c',
    borderRadius: 8,
    padding: '20px 22px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
  },
  title: {
    fontSize: 17,
    fontWeight: 600,
    color: '#7f1d1d',
    marginBottom: 8,
  },
  body: {
    fontSize: 14,
    lineHeight: 1.55,
    color: '#3f3f46',
    margin: '0 0 14px',
  },
  link: {
    color: '#b45309',
    textDecoration: 'none',
    borderBottom: '1px dashed rgba(180,83,9,0.5)',
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
  },
  primary: {
    background: '#f59e0b',
    color: '#18181b',
    border: 'none',
    padding: '8px 14px',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  },
  secondary: {
    background: '#fff',
    color: '#3f3f46',
    border: '1px solid #d4d4d8',
    padding: '8px 14px',
    borderRadius: 4,
    fontSize: 13,
    cursor: 'pointer',
  },
  tertiary: {
    color: '#52525b',
    fontSize: 12,
    textDecoration: 'none',
    padding: '8px 6px',
  },
  details: {
    marginTop: 14,
    fontSize: 12,
    color: '#52525b',
  },
  summary: {
    cursor: 'pointer',
    userSelect: 'none',
  },
  pre: {
    background: '#f4f4f5',
    padding: 10,
    borderRadius: 4,
    fontSize: 11,
    overflow: 'auto',
    maxHeight: 200,
    whiteSpace: 'pre-wrap',
    marginTop: 8,
  },
};
